import { Box } from 'src/design/atoms/Box';
import { Loader } from 'src/design/atoms/Loader';
import { Paper } from 'src/design/atoms/Paper';
import { BasicButton } from 'src/design/atoms/BasicButton';
import { ButtonGroup } from 'src/design/atoms/ButtonGroup';
import React from 'react';
import { PostDto, PostFilterDto } from 'src/api/FiretrolApiClient';
import { usePostSearch } from 'src/api/queries/posts/usePostSearch';
import { EditPost } from 'src/routes/Jobs/ActivityFeed/components/EditPost';
import { PostCard } from 'src/routes/Jobs/ActivityFeed/components/PostCard';
import { PostItem } from 'src/routes/Jobs/ActivityFeed/components/PostItem';

interface IRelatedFeedFooterProps {
  jobId: number;
  /** Resource-scoped filter fragment, e.g. `{ changeId }` or `{ transmittalId }`. */
  filter: Partial<PostFilterDto>;
  /** When false the footer renders nothing — used before the resource id exists. */
  enabled: boolean;
}

/** Read-only activity/feed footer for a single workflow resource (Change, Transmittal, …), scoped by the resource id supplied in `filter`. */
export const RelatedFeedFooter: React.FC<IRelatedFeedFooterProps> = ({
  jobId,
  filter,
  enabled,
}) => {
  const [feedTab, setFeedTab] = React.useState('activity');
  const [openPost, setOpenPost] = React.useState<PostDto | null>(null);

  // React Query hashes the key structurally, so the inline filter object's
  // identity does not re-trigger the query — only its contents do.
  const { data, isFetching } = usePostSearch(enabled ? jobId : null, {
    filters: {
      visibility: 'all',
      resolved: 'unresolved',
      assignedToMe: false,
      createdByMe: false,
      constraint: false,
      critical: false,
      ctc: false,
      cost: false,
      assigned: false,
      ...filter,
    } as PostFilterDto,
    sort: 'updated_at',
    postType: 'all',
    query: '',
  });
  const posts = data?.pages.flatMap((page) => page.data) ?? [];

  const renderPosts = () => {
    if (posts.length === 0) {
      return (
        <Box component="h3" ta="center">
          No posts
        </Box>
      );
    }
    return posts.map((post) => (
      <PostItem
        key={post.id}
        item={post}
        openEditDialog={setOpenPost}
        jobId={jobId}
      />
    ));
  };

  if (!enabled) return null;

  return (
    <Paper mt="md">
      <EditPost
        post={openPost}
        handleClose={() => setOpenPost(null)}
        onPostSaved={() => setOpenPost(null)}
      />
      <ButtonGroup mb="md">
        <BasicButton
          variant={feedTab === 'activity' ? 'filled' : 'outline'}
          size="sm"
          onClick={() => setFeedTab('activity')}
        >
          Activity
        </BasicButton>
        <BasicButton
          variant={feedTab === 'feed' ? 'filled' : 'outline'}
          size="sm"
          onClick={() => setFeedTab('feed')}
        >
          Feed
        </BasicButton>
      </ButtonGroup>
      {feedTab === 'activity' && <PostCard onPostCreated={() => undefined} />}
      {feedTab === 'feed' && (
        <Box maw="50%" mx="auto">
          {isFetching && <Loader />}
          {renderPosts()}
        </Box>
      )}
    </Paper>
  );
};
